/**
 * Lightweight wrapper around Google Analytics 4 (gtag.js).
 *
 * GA4 measurement ID is loaded in index.html. This module:
 *   - Exposes typed helpers for pageviews + custom events
 *   - Fires the GA4 recommended `generate_lead` conversion event for form submissions
 *   - Buffers calls if gtag hasn't loaded yet (resilient to script async loading)
 *
 * Mark `generate_lead` as a Conversion in GA4 Admin > Events to track in Search Console
 * + Google Ads. Search Console rankings/CTR are tracked automatically once the property
 * is verified (see <meta name="google-site-verification"> in index.html).
 */

export const GA_MEASUREMENT_ID = "G-L6QPP0YYTP";

declare global {
  interface Window {
    dataLayer: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

type GtagArgs = unknown[];

function callGtag(...args: GtagArgs) {
  if (typeof window === "undefined") return;
  // Always push to dataLayer so events queue even before gtag.js finishes loading.
  window.dataLayer = window.dataLayer || [];
  if (typeof window.gtag === "function") {
    window.gtag(...(args as Parameters<NonNullable<Window["gtag"]>>));
  } else {
    window.dataLayer.push(args);
  }
}

/** Manual SPA pageview — call on every route change. */
export function trackPageview(path: string, title?: string) {
  callGtag("event", "page_view", {
    page_path: path,
    page_location: window.location.href,
    page_title: title ?? document.title,
    send_to: GA_MEASUREMENT_ID,
  });
}

/** Generic custom event. Use snake_case event names per GA4 conventions. */
export function trackEvent(
  eventName: string,
  params: Record<string, unknown> = {},
) {
  callGtag("event", eventName, params);
}

/**
 * Track a lead/form submission as a GA4 conversion.
 *
 * Mark `generate_lead` as a Conversion in GA4 Admin so it counts toward
 * Google Ads conversions and Search Console attribution.
 */
export function trackLead(params: {
  formType: string;
  /** Estimated lead value in GBP — helps optimise ROAS in GA4 / Google Ads. */
  value?: number;
  currency?: string;
  service?: string;
  source?: string;
  [k: string]: unknown;
}) {
  const { value = 250, currency = "GBP", ...rest } = params;
  trackEvent("generate_lead", {
    value,
    currency,
    ...rest,
  });
  // Also fire a generic form_submit event for funnel analysis.
  trackEvent("form_submit", { ...rest, value, currency });
}

/** CTA click — useful for measuring button effectiveness across the site. */
export function trackCTAClick(label: string, location: string) {
  trackEvent("cta_click", { cta_label: label, cta_location: location });
}
